import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Star, TrendingUp } from "lucide-react";
import { MenuItem, CartItem } from "@/types";
import { menuItems } from "@/data/menuItems";
import { getMenuItemImageUrl } from "@/services/utlits";

interface SuggestedItemsProps {
  recentlyAdded?: CartItem;
  onAddToCart: (item: CartItem) => void;
}

export function SuggestedItems({
  recentlyAdded,
  onAddToCart,
}: SuggestedItemsProps) {
  const [suggestions, setSuggestions] = useState<MenuItem[]>([]);

  useEffect(() => {
    if (recentlyAdded) {
      // Mock suggestions - in real app would fetch related items from API
      setSuggestions([]);
    }
  }, [recentlyAdded]);

  if (!recentlyAdded || suggestions.length === 0) {
    return null;
  }

  const handleQuickAdd = (item: MenuItem) => {
    const cartItem: CartItem = {
      ...item,
      quantity: 1,
      totalPrice: item?.basePrice,
    };
    onAddToCart(cartItem);
  };
  return (
    <Card className="mt-4 border border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="flex items-center space-x-2 text-orange-800">
          <div className="w-4 h-4 bg-gradient-to-r from-orange-500 to-yellow-500 rounded flex items-center justify-center">
            <TrendingUp className="w-2.5 h-2.5 text-white" />
          </div>
          <span className="text-xs font-semibold">You might also like</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 px-3 pb-3">
        <div className="space-y-2">
          {suggestions.map((item) => (
            <div
              key={item.id}
              className="flex items-center space-x-2 p-2 bg-white rounded-lg border border-orange-100 hover:border-orange-200 transition-all duration-200"
            >
              <img
                src={getMenuItemImageUrl(item.imageUrl)}
                alt={item.name}
                className="w-8 h-8 object-cover rounded flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-xs text-gray-900 line-clamp-1 mb-0.5">
                  {item.name}
                </h4>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-emerald-600">
                    ${item?.basePrice?.toFixed(2)}
                  </span>
                  {item.isPopular && (
                    <Badge className="bg-orange-100 text-orange-800 text-xs px-1 py-0 h-4">
                      <Star className="w-2 h-2 mr-0.5 fill-current" />
                      Hot
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                onClick={() => handleQuickAdd(item)}
                size="sm"
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white h-6 w-6 p-0 text-xs rounded-md"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
